import React from 'react';
import { Box, Paper, Stack, Button, StackProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from '@/components/media/iconify';

export type FilterResultProps = {
  canReset: boolean;
  onResetFilters: VoidFunction;
  children: React.ReactNode;
};

export function FilterResult({ canReset, onResetFilters, children }: FilterResultProps) {
  const { t } = useTranslation();

  return (
    <Stack
      sx={{
        px: { xs: 2, sm: 2 },
        pb: { xs: 1.5, sm: 2 },
        pt: { xs: 0.5, sm: 0 },
      }}
      spacing={{ xs: 1, sm: 1.5 }}
    >
      <Stack
        flexGrow={1}
        spacing={{ xs: 0.75, sm: 1 }}
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        sx={{ gap: { xs: 0.75, sm: 1 } }}
      >
        {children}
        {canReset && (
          <Button
            color="error"
            onClick={onResetFilters}
            startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
            sx={{ flexShrink: 0 }}
          >
            {t('Clear')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export function FilterResultBlock({
  label,
  children,
  sx,
  ...other
}: StackProps & { label: string }) {
  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={{ xs: 0.75, sm: 1 }}
      direction="row"
      sx={{
        p: { xs: 0.75, sm: 1 },
        borderRadius: { xs: 1.5, sm: 1 },
        overflow: 'hidden',
        borderStyle: 'dashed',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        ...sx,
      }}
      {...other}
    >
      <Box
        component="span"
        sx={{
          typography: 'subtitle2',
          fontSize: { xs: '0.875rem', sm: '0.875rem' },
          minWidth: 'fit-content',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {label}
      </Box>
      <Stack
        spacing={{ xs: 0.5, sm: 1 }}
        direction="row"
        flexWrap="wrap"
        sx={{ gap: { xs: 0.5, sm: 1 }, alignItems: 'center' }}
      >
        {children}
      </Stack>
    </Stack>
  );
}
